import { AfterViewInit, Component, OnDestroy } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TestMicrophoneReturn } from "@zoom/videosdk";
import { Subject, takeUntil } from "rxjs";
import { ZoomLocalSettingsService } from "src/app/zoom/zoom-local-settings.service";
import { ZoomService } from "src/app/zoom/zoom.service";

enum AudioTestState {
    IDLE,
    SPEAKER,
    MICROPHONE,
    PLAYING_RECORD,
}

@Component({
    selector: "app-audio-settings",
    templateUrl: "./audio-settings.component.html",
    styleUrls: ["./audio-settings.component.scss"],
})
export class AudioSettingsComponent implements AfterViewInit, OnDestroy {
    /** Current state */
    public currentState: AudioTestState = AudioTestState.IDLE;

    /** True if no sound is detected */
    public noSoundDetected: boolean = false;

    /** true when no mic is detected */
    public noMicrophone: boolean = false;

    /** All microphone devices */
    public microphoneDevices: Array<MediaDeviceInfo> = [];

    /** All speaker devices */
    public speakerDevices: Array<MediaDeviceInfo> = [];

    /** Selected microphone id */
    public selectedMicrophoneId: string;

    /** Selected speaker id */
    public selectedSpeakerId: string;

    /** Tester zoom for microphone */
    private microphoneTester: TestMicrophoneReturn;

    /** Wave container element */
    private waveContainer: HTMLElement;

    /** HTMLElement use to draw the waves sound */
    private waveElements: HTMLCollectionOf<Element>;

    /** Recorded frequency, use to play the wave animation when listenning the microphone test */
    private recordedFreq: Array<number> = [];

    /** Total of all mean frequencies received */
    private totalMeanFreq: number = 0;

    public AudioTestState = AudioTestState; // Use for the template

    private destroy$ = new Subject();

    constructor(
        private activeModal: NgbActiveModal,
        private zoomLocalSettingsService: ZoomLocalSettingsService,
        private zoomService: ZoomService,
    ) {
        this.zoomLocalSettingsService.onDeviceChanged
            .pipe(takeUntil(this.destroy$))
            .subscribe((e) => {
                this.getAllDevicesInput();
                this.getAllDevicesOutput();
            });
    }

    ngOnDestroy(): void {
        if (this.microphoneTester) this.microphoneTester.destroy();
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        if (this.isSafari) return;
        this.waveElements = document.getElementsByClassName("wave");
        this.waveContainer = <HTMLElement>(
            document.getElementsByClassName("waves")[0]
        );

        // Getting all devices
        this.getAllDevicesInput();
        this.getAllDevicesOutput();

        // set the selected devices
        this.selectedMicrophoneId =
            this.zoomLocalSettingsService.microphoneDeviceId;
        this.selectedSpeakerId = this.zoomLocalSettingsService.speakerDeviceId;

        // Test if no microphone are detected
        setTimeout(() => {
            if (this.microphoneDevices.length === 0) {
                this.noMicrophone = true;
            }
        }, 1000);
    }

    /**
     * Try to get all inputdevices
     */
    getAllDevicesInput() {
        ZoomLocalSettingsService.getDevicesByKind("audioinput").then(
            (devices) => {
                this.microphoneDevices = devices;
                this.noMicrophone = false;
                if (devices) {
                    const index = devices
                        .map((device) => device.deviceId)
                        .indexOf(this.selectedMicrophoneId);
                    this.selectedMicrophoneId =
                        index > -1 ? this.selectedMicrophoneId : "default";
                }
            },
        );
    }

    /**
     * Get all device output
     */
    getAllDevicesOutput() {
        ZoomLocalSettingsService.getDevicesByKind("audiooutput")
            .then((devices) => {
                this.speakerDevices = devices;
            })
            .finally(() => {
                // If no device, setup a default one
                if (!this.speakerDevices || this.speakerDevices.length === 0) {
                    this.speakerDevices = [
                        {
                            label: "Default",
                            deviceId: "default",
                            groupId: "default",
                            kind: "audiooutput",
                            toJSON: () => {},
                        },
                    ];
                }
                const index = this.speakerDevices
                    .map((device) => device.deviceId)
                    .indexOf(this.selectedSpeakerId);
                this.selectedSpeakerId =
                    index > -1 ? this.selectedSpeakerId : "default";
            });
    }

    /**
     * When the user click on 'Done'
     * Validate the change
     */
    onDoneClicked() {
        if (!this.isSafari) {
            this.zoomLocalSettingsService.setMicrophoneDevice(
                this.selectedMicrophoneId,
            );
            this.zoomLocalSettingsService.setSpeakerDevice(
                this.selectedSpeakerId,
            );
        }
        this.activeModal.close();
    }

    /**
     * When the user change his microphone
     * @param newDeviceID
     */
    onMicrophoneChanged(newDeviceID) {
        this.selectedMicrophoneId = newDeviceID;
    }

    /**
     * When the user change his speaker
     * @param newDeviceID
     */
    onSpeakerChanged(newDeviceID) {
        this.selectedSpeakerId = newDeviceID;
    }

    /**
     * When the user click to test his microphone
     * Record during 5s, then play it back
     */
    onTestMicrophoneClicked() {
        this.displayWaveSound(true);
        this.currentState = AudioTestState.MICROPHONE;
        this.totalMeanFreq = 0;

        const options = {
            onAnalyseFrequency: this.onAnalyseFrequency.bind(
                this,
                100,
                true,
                false,
            ),
            recordAndPlay: true,
            maxRecordDuration: 5000, // 5s of record
            onStartPlayRecording: this.onStartPlayRecording.bind(this),
            onStopPlayRecording: this.stopTestMicrophone.bind(this),
            microphoneId: this.selectedMicrophoneId,
        };

        this.microphoneTester =
            this.zoomLocalSettingsService.testMicrophone(options);
    }

    /**
     * When the user click to test his speaker
     * Play a short sound
     */
    onTestSpeakerClicked() {
        this.displayWaveSound(true);
        this.currentState = AudioTestState.SPEAKER;
        this.noSoundDetected = false;

        const options = {
            onAnalyseFrequency: this.onAnalyseFrequency.bind(
                this,
                30,
                false,
                false,
            ),
            speakerId: this.selectedSpeakerId,
        };

        this.zoomLocalSettingsService.testSpeaker(options).then(() => {
            this.currentState = AudioTestState.IDLE;
            this.resetSoundWaves();
        });
    }

    /**
     * Stop test from microphone
     */
    private stopTestMicrophone() {
        this.recordedFreq = [];
        this.resetSoundWaves();
        this.currentState = AudioTestState.IDLE;

        try {
            this.microphoneTester.destroy();
        } catch (e) {}
    }

    /**
     * Start playing the recording
     */
    private onStartPlayRecording() {
        if (this.noSoundDetected) {
            this.stopTestMicrophone();
        } else {
            this.currentState = AudioTestState.PLAYING_RECORD;
            this.playback();
        }
    }

    /**
     * Self loop to play the sound animation from the recorded frequencies
     */
    private playback() {
        if (
            this.currentState === AudioTestState.PLAYING_RECORD &&
            this.recordedFreq.length > 0
        ) {
            this.onAnalyseFrequency(
                100,
                false,
                true,
                this.recordedFreq.shift(),
            );
            setTimeout(this.playback.bind(this), 20);
        }
    }

    /**
     * Render sound waves animation
     * Call when received a mean frequency
     *
     * @param maxFreq : Max Frequency
     * @param record : If true the frequency will be stored in recordedFreq
     * @param isPlayback : True if it's call for the playback (microphone test)
     * @param freq : Frequency to display
     * @returns void
     */
    private onAnalyseFrequency(
        maxFreq: number,
        record: boolean,
        isPlayback: boolean,
        freq: number,
    ) {
        // If we are playing a record, don't handle the frequency from the microphone
        if (this.currentState === AudioTestState.PLAYING_RECORD && !isPlayback)
            return;

        // Test if we detect some sound from the microphone
        if (this.currentState === AudioTestState.MICROPHONE) {
            this.totalMeanFreq += freq;
            this.noSoundDetected = this.totalMeanFreq < 200;
        }

        // Store frequency
        if (record) this.recordedFreq.push(freq);

        const maxWaveIndex = this.waveElements.length;
        const indexFreq =
            Math.min(
                Math.floor((freq / maxFreq) * maxWaveIndex),
                maxWaveIndex,
            ) - 1;
        let element: HTMLElement;

        for (let i = 0; i < this.waveElements.length; i++) {
            // For each wave element
            element = <HTMLScriptElement>this.waveElements[i];
            if (i === indexFreq) {
                // if the element correspond to the current mean freq => 100%
                element.style.height = "100%";
            } else {
                let height = element.style.height
                    ? Number(element.style.height.replace("%", ""))
                    : 0;
                height *= 0.9; // slowly lower the bar
                if (indexFreq > -1) {
                    // If we are detecting some sound
                    const spread = 25 / (i - indexFreq) ** 2; // spread around the current mean freq
                    height = Math.min(spread + height, 100);
                }
                element.style.height = `${height}%`;
            }
        }
    }

    /**
     * Reset Sound wave animation
     */
    private resetSoundWaves() {
        for (let i = 0; i < this.waveElements.length; i++) {
            const element = <HTMLScriptElement>this.waveElements[i];
            element.style.height = "0%";
        }
    }

    /**
     * Display or hide the wave sound element
     * @param show true or false
     */
    public displayWaveSound(show: boolean) {
        if (show) {
            this.waveContainer.className = this.waveContainer.className.replace(
                "hidden",
                "show",
            );
        } else {
            this.waveContainer.className = this.waveContainer.className.replace(
                "show",
                "hidden",
            );
        }
    }

    /**
     * Return true if the user is on safari
     */
    get isSafari(): boolean {
        return this.zoomService.isSafari;
    }
}
